import React from 'react';
import { classNames } from '~/utils/classNames';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { SHOW_ALERT_LIMIT } from '@/utils/constants';
import { useLocation } from '@remix-run/react';

type PromptQuotaIndicatorProps = {
  className?: string;
  variant?: 'default' | 'prechat';
  used: number;
  total: number;
  isSubscribed: boolean;
  isProPlan: boolean;
  isLaunchPlan: boolean;
  onOpenUpgrade: (mode: 'upgrade' | 'topup') => void;
};

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

export function PromptQuotaIndicator({
  className,
  variant = 'default',
  used,
  total,
  isSubscribed,
  isProPlan,
  isLaunchPlan,
  onOpenUpgrade,
}: PromptQuotaIndicatorProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const isChatRoute = location.pathname === '/chat' || location.pathname.startsWith('/chat/');

  if (isLaunchPlan) {
    return null;
  }

  const safeTotal = Math.max(0, total);
  const safeUsed = Math.max(0, used);

  if (safeTotal <= 0) {
    return null;
  }

  const remaining = Math.max(safeTotal - safeUsed, 0);
  const exhausted = safeUsed >= safeTotal;

  if (variant === 'prechat') {
    if (isSubscribed || remaining > SHOW_ALERT_LIMIT) {
      return null;
    }

    return (
      <div className={classNames('w-[90%] mx-auto', className)}>
        <div
          className={classNames(
            'h-[34px] px-4 flex items-center gap-2',
            'rounded-b-2xl border border-[var(--dualite-border-primary)]',
            'bg-[var(--dualite-background-secondary)]',
            'text-[13px] leading-none',
          )}
        >
          {/* icon */}
          <div className="i-ph:star-four-fill text-[#7C6BFF] text-[13px] shrink-0" aria-hidden="true" />

          {/* text */}
          <div className="truncate text-[#7C6BFF]">
            <span className="font-medium">{remaining} messages left</span>{' '}
            <button
              type="button"
              onClick={() => onOpenUpgrade('upgrade')}
              className="font-medium text-[#7C6BFF] underline underline-offset-[2px] hover:opacity-70 transition-opacity"
              style={{ textDecorationThickness: '1px' }}
            >
              {t('chat.quota.upgradeYourPlanToGetMore')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Keep the input clean; only surface when it matters.
  const shouldShow = exhausted || remaining <= SHOW_ALERT_LIMIT;

  if (!shouldShow) {
    return null;
  }

  const ctaMode: 'upgrade' | 'topup' = isSubscribed && isProPlan ? 'topup' : 'upgrade';
  const showCTA = !isSubscribed || isProPlan;
  const ctaLabel = exhausted
    ? t('chat.quota.ctaExhausted')
    : ctaMode === 'topup'
      ? t('chat.quota.ctaTopUp')
      : t('chat.quota.ctaUpgrade');

  if (isChatRoute) {
    const isWarn = exhausted;

    const toneText = 'text-[#7C6BFF]';
    const toneBorder = 'border-[var(--dualite-border-primary)]';
    const toneBg = 'bg-[var(--dualite-background-secondary)]';

    return (
      <div className={classNames('relative', className)}>
        <div
          className={classNames(
            'w-full h-[74px] flex items-start gap-3 px-5 pt-[12px] -mt-[10px] mb-[-30px]',
            'rounded-t-[18px] rounded-b-none',
            'border border-b-0',
            toneBorder,
            toneBg,
            'text-[13px]',
          )}
        >
          {/* icon */}
          <div className={classNames('i-ph:star-four-fill text-[13px] shrink-0 pt-[18px]', toneText)} />

          {/* text */}
          <div className={classNames('truncate font-medium', toneText)}>
            {exhausted ? t('chat.quota.exhausted') : t('chat.quota.lowRemaining', { remaining })}

            {showCTA && (
              <button
                type="button"
                onClick={() => onOpenUpgrade(ctaMode)}
                className={classNames(
                  'ml-1 underline underline-offset-2 transition-opacity hover:opacity-70',
                  toneText,
                )}
              >
                {ctaLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('relative z-30 w-full -mb-[1px]', className)}>
      <div
        className={classNames(
          'h-[46px] w-full flex items-center gap-3 px-5',
          'rounded-t-lg rounded-b-none',
          'bg-[var(--dualite-background-secondary)]',
          'border border-[var(--dualite-border-primary)] border-b-0',
          'text-[13px]',
        )}
      >
        {/* icon */}
        <div className="i-ph:star-four-fill text-[#7C6BFF] text-[13px] shrink-0" />

        {/* text */}
        <div className="truncate font-medium text-[#7C6BFF]">
          Only {remaining} messages left{' '}
          <button
            type="button"
            onClick={() => onOpenUpgrade(ctaMode)}
            className="text-[#7C6BFF] underline underline-offset-[2px] hover:opacity-70 transition-opacity"
            style={{ textDecorationThickness: '1px' }}
          >
            Upgrade
          </button>
        </div>
      </div>
    </div>
  );
}
